import { useAssistant } from '@utilities/hooks';
import { ChatMessageType } from '@utilities/types';
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';

export const useController = () => {

    const {
        messages, setMessages,
        getInitChatError, getInitChatLoading,
        sendMessageError, sendMessageLoading,
        onInitChat, onSendMessage,
    } = useAssistant();

    const messagesEndRef = useRef<HTMLDivElement>(null);

    const [isChatVisible, setChatVisible] = useState(false);
    const [currentMessage, setCurrentMessage] = useState("");
    const [error, setError] = useState<string | null>(null);

    const isLoadingChat = useMemo(()=> (
        getInitChatLoading || sendMessageLoading
    ), [getInitChatLoading, sendMessageLoading]);

    useEffect(()=> {
        if(messages.length <= 0){
            onInitChat();
        }
    }, [messages]);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, isLoadingChat, isChatVisible]);

    useEffect(()=> {
        if(getInitChatError){
            setError(getInitChatError.message);
        } else if(sendMessageError){
            setError(sendMessageError.message);
        }
    }, [getInitChatError, sendMessageError]);

    const toggleChat = useCallback(() => {
        setChatVisible(!isChatVisible);
        setError(null);
    }, [isChatVisible]);

    const handleRetry = useCallback(async()=> {
        if(getInitChatError){
            setError(null);
            onInitChat();
        } else if(sendMessageError){
            setError(null);
            await onSendMessage(messages);
        }
    }, [
        getInitChatError, messages, sendMessageError,
        onInitChat, onSendMessage,
    ]);

    const handleSendMessage = useCallback(async () => {
        if(!currentMessage) return;

        const newMessage:ChatMessageType = {
            id: Date.now().toString(),
            sender: 'user',
            message: currentMessage,
            date: new Date(),
            status: 'sent',
        };

        setMessages((prevMessages) => [...prevMessages, newMessage]);
        setCurrentMessage("");
        await onSendMessage([...messages, newMessage]);
    }, [currentMessage, messages, onSendMessage, setMessages]);

    const handleClearChat = useCallback(() => {
        setMessages(prev=> prev.filter((msg) => msg.sender === 'system'));
    }, [setMessages]);

    return {
        isChatVisible, currentMessage,
        messages, isLoadingChat,
        error, messagesEndRef,
        toggleChat, handleRetry, handleClearChat,
        setCurrentMessage, handleSendMessage,
    };
};
